import React, { useState, useEffect } from 'react';
import { X, Receipt, Loader2, Calendar, User, Briefcase, ExternalLink, AlertTriangle } from 'lucide-react';
import { paymentBatchService } from '../../../services/paymentBatchService';
import { formatCurrency, calculateInstanceFinancials } from '../../../utils/earningsCalculations';

/**
 * PaymentDetailsModal
 *
 * Modal for viewing payment batch details including:
 * - Proof of payment image
 * - Transaction reference
 * - List of all jobs included in the batch with contractor names
 * - Action to revert entire payment batch
 *
 * Note: Individual job removal is intentionally not supported to maintain
 * data integrity between bank transactions and recorded payments.
 */
export default function PaymentDetailsModal({
  jobInstanceId,
  onClose,
  onRevert
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);

  useEffect(() => {
    const loadBatchDetails = async () => {
      if (!jobInstanceId) return;

      setLoading(true);
      setError(null);

      try {
        const batchData = await paymentBatchService.getBatchForJobInstance(jobInstanceId);
        setBatch(batchData);
      } catch (err) {
        console.error('Error loading payment batch:', err);
        setError(err.message || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    loadBatchDetails();
  }, [jobInstanceId]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle revert entire payment batch
  const handleRevertPayment = async () => {
    if (!batch?.id) return;

    setReverting(true);
    try {
      await paymentBatchService.revertPaymentBatch(batch.id);
      onRevert?.();
      onClose();
    } catch (err) {
      console.error('Error reverting payment:', err);
      setError('Failed to revert payment. Please try again.');
    } finally {
      setReverting(false);
      setShowRevertConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 pt-20 px-4 pb-4 overflow-y-auto">
      <div className="bg-background w-full max-w-2xl max-h-[calc(100vh-6rem)] rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Receipt className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Payment Details</h2>
              <p className="text-text-secondary text-sm">
                {batch ? `Transaction: ${batch.transaction_ref}` : 'Loading...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={reverting}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-400 mb-2">{error}</p>
              <button
                onClick={onClose}
                className="text-text-secondary hover:text-text-primary"
              >
                Close
              </button>
            </div>
          ) : !batch ? (
            <div className="text-center py-12">
              <p className="text-text-secondary mb-2">No payment batch found for this job.</p>
              <p className="text-text-tertiary text-sm">This job may have been marked as paid before the batch payment system was implemented.</p>
            </div>
          ) : (
            <>
              {/* Payment Info */}
              <div className="mb-6 p-4 bg-surface rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Transaction Reference</p>
                    <p className="text-text-primary font-mono font-medium">{batch.transaction_ref}</p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Total Amount</p>
                    <p className="text-text-primary font-bold text-lg">{formatCurrency(batch.total_amount)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Payment Date</p>
                    <p className="text-text-primary flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(batch.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-text-secondary mb-1">Recorded By</p>
                    <p className="text-text-primary flex items-center gap-1">
                      <User size={14} />
                      {batch.created_by_user?.full_name || 'Unknown'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Proof of Payment */}
              {batch.proof_url && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-text-secondary mb-3">Proof of Payment</h3>
                  <div className="relative group">
                    <img
                      src={batch.proof_url}
                      alt="Proof of payment"
                      className="w-full max-h-64 object-contain rounded-lg border border-border bg-surface"
                    />
                    <a
                      href={batch.proof_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 p-2 bg-black/60 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              )}

              {/* Jobs in this Batch */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-text-secondary mb-3">
                  Jobs Included ({batch.timesheets?.length || 0})
                </h3>
                <div className="space-y-2">
                  {batch.timesheets?.map((timesheet) => {
                    // Calculate earnings using the standard calculation (min 1hr rule)
                    const instanceForCalc = {
                      timesheet: [timesheet],
                      overtime_request: timesheet.job_instance?.overtime_request
                    };
                    const job = timesheet.job_instance?.job;
                    const financials = calculateInstanceFinancials(instanceForCalc, job);
                    const calculatedAmount = (financials.payoutToContractor || 0) +
                      (financials.hasOvertimeApproved ? (financials.overtimePayoutToContractor || 0) : 0);

                    return (
                      <div
                        key={timesheet.id}
                        className="flex items-center justify-between p-3 bg-surface rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="text-text-primary font-medium">
                              {timesheet.job_instance?.job?.job_name || 'Unknown Job'}
                            </p>
                            <p className="text-text-secondary text-sm flex items-center gap-2">
                              <User size={12} />
                              {timesheet.contractor?.full_name || 'Unknown Contractor'}
                              <span className="text-text-tertiary">•</span>
                              {timesheet.job_instance?.scheduled_date
                                ? new Date(timesheet.job_instance.scheduled_date).toLocaleDateString('en-AU', {
                                    day: 'numeric',
                                    month: 'short'
                                  })
                                : '—'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-text-primary font-medium">
                            {formatCurrency(calculatedAmount)}
                          </p>
                          <p className="text-text-tertiary text-xs">
                            {timesheet.job_instance?.job?.job_display_id || ''}
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  {(!batch.timesheets || batch.timesheets.length === 0) && (
                    <p className="text-text-secondary text-center py-4">
                      No job details available
                    </p>
                  )}
                </div>
              </div>

              {/* Revert Payment Section */}
              {onRevert && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-text-primary font-medium mb-1">Revert Payment</h4>
                      <p className="text-text-secondary text-sm mb-3">
                        This will mark all {batch.timesheets?.length || 0} job(s) as unpaid and delete this payment record.
                        Contractors will need to re-confirm payment receipt.
                      </p>
                      <button
                        onClick={() => setShowRevertConfirm(true)}
                        disabled={reverting}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        {reverting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" />
                            Reverting...
                          </span>
                        ) : (
                          'Revert Entire Payment'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex-shrink-0">
          <button
            onClick={onClose}
            disabled={reverting}
            className="w-full px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors disabled:opacity-50"
          >
            Done
          </button>
        </div>
      </div>

      {/* Revert Confirmation Dialog */}
      {showRevertConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-background w-full max-w-md rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-bold text-text-primary">Confirm Revert</h3>
            </div>
            <p className="text-text-secondary mb-6">
              Are you sure you want to revert this payment? This will:
            </p>
            <ul className="text-text-secondary text-sm mb-6 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                Mark all {batch?.timesheets?.length || 0} job(s) as unpaid (approved status)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                Delete the payment record and proof of payment
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400">•</span>
                Reset contractor payment confirmations
              </li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRevertConfirm(false)}
                disabled={reverting}
                className="flex-1 px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRevertPayment}
                disabled={reverting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {reverting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Reverting...
                  </span>
                ) : (
                  'Revert Payment'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
